/**
 * useProductionAnalysis — load DATA sheet "Production Analysis" của 1 PO từ
 * BACKEND (`GET …/batch-report/{po}/production-analysis`). BE tính theo rule
 * Excel từ 2 view Batch Report (`v_batchreport_issue`=INPUT, `v_batchreport_receive`
 * =OUTPUT) + BOM recipe — xem `factory_batch_report_service.get_production_analysis`.
 * KHÔNG còn tính/mock ở FE.
 *
 * Vẫn gọi [[useBomReportData]] cho Block Overview (LIFFE/Diff/Input-Output codes,
 * dùng chung endpoint với tab BOM).
 */
import { useMemo } from 'react'

import { useGetProductionAnalysisQuery } from '../../../../store/api/factoryApi'
import type { BudgetActualRow, ExtractedValueSummary, LossSummary } from '../productionReportMock'
import { useBomReportData, type BomReportData } from './useBomReportData'

const EMPTY_LOSS: LossSummary = {
  totalLoss: 0, totalLossPct: 0, mcIn: 0, mcOut: 0, mcLoss: 0, mcLossPct: 0,
  unexpLoss: 0, unexpLossPct: 0, bomLoss: 0, variance: 0,
}
const EMPTY_EXTRACTED: ExtractedValueSummary = {
  valueIn: 0, valueOut: 0, valueExtracted: 0, valueExtractedPerMt: 0, handling: 0,
  valueExtractedPerMtAfterCost: 0, actualExtracted: 0, bomExtracted: 0, pnlPerMt: 0, totalPnl: 0,
}

export interface ProductionAnalysisData {
  bom: BomReportData
  inputRows: BudgetActualRow[]
  outputRows: BudgetActualRow[]
  loss: LossSummary
  extracted: ExtractedValueSummary
  /** Source = distinct origin_source ở INPUT (BE join ' | '). */
  source: string
  /** PO có stack thật (issue/receive) → có data để hiển thị. */
  hasData: boolean
  isLoading: boolean
}

export function useProductionAnalysis(args: {
  factoryCode?: string
  bomCode?: string
  poNo?: string
  poDateISO?: string
}): ProductionAnalysisData {
  const factoryCode = args.factoryCode ?? ''
  const poNo = args.poNo?.trim() ?? ''
  const bom = useBomReportData(args)

  const { data, isFetching } = useGetProductionAnalysisQuery(
    { factoryCode, poNo },
    { skip: !factoryCode || !poNo },
  )

  return useMemo(() => {
    const inputRows = (data?.inputRows ?? []) as unknown as BudgetActualRow[]
    const outputRows = (data?.outputRows ?? []) as unknown as BudgetActualRow[]
    return {
      bom,
      inputRows,
      outputRows,
      loss: (data?.loss as unknown as LossSummary) ?? EMPTY_LOSS,
      extracted: (data?.extracted as unknown as ExtractedValueSummary) ?? EMPTY_EXTRACTED,
      source: data?.source ?? '',
      hasData: inputRows.length > 0 || outputRows.length > 0,
      isLoading: isFetching || bom.isLoading,
    }
  }, [data, bom, isFetching])
}
